import { commonOptions } from '@/config/options'
import type { PageContext } from '@zto/zpage'
import dayjs from 'dayjs'
import { RbacRoleScopeType } from '@/config/enums'

// 新增、编辑商品
export const addEditDialog = (isEdit: Boolean) => {
  return {
    width: 800,
    bodyHeight: '60vh',
    top: '10vh',
    labelWidth: 100,
    itemSpan: 24,

    onShow: async ({ app, data }: PageContext) => {
      const basic = app.stores.userStore?.data?.basic || {}
      let other = {}
      if (basic.belongType === RbacRoleScopeType.SUPPLIER) {
        other = {
          supplierCode: basic.supplierOrgCode,
          supplierName: basic.supplierOrgName,
          supplierDisabled: true
        }
      }
      if (!isEdit) {
        return { payload: { ...other } }
      }
      const res = await app.request({
        api: '/cabinet/admin/pms/AdminPmsProductQueryController/detail',
        params: { productCode: data.productCode }
      })

      return {
        payload: {
          ...res,
          ...other
        }
      }
    },

    formItems: [
      {
        type: 'dis-input',
        prop: 'productCode',
        label: '商品编号',
        visibleOn: `${isEdit}` // 编辑时 显示
      },

      { type: 'input', prop: 'productName', label: '商品名称', maxlength: 50, trim: true, required: true },
      {
        type: 'fuzzy-select',
        api: '/cabinet/admin/base/AdminBaseSupplierQueryController/queryList',
        apiParams: { current: 1, size: 500 },
        prop: 'supplierCode',
        labelProp: 'supplierName',
        label: '供应商',
        optionValueProp: 'supplierCode',
        optionLabelProp: 'supplierName',
        triggerFocus: true,
        remote: true,
        keywordProp: 'supplierName',
        placeholder: '请选择供应商',
        required: true,
        dynamicAttrs: ({ data }: PageContext) => {
          return {
            disabled: data.supplierDisabled
          }
        },
        span: 12
      },
      { type: 'input', prop: 'productSpec', label: '规格', maxlength: 20, trim: true, span: 12 },
      {
        type: 'input-number',
        prop: 'costPrice',
        label: '成本价（元）',
        min: 0.01,
        max: 99999.99,
        precision: 2,
        required: true,
        span: 8
      },
      {
        type: 'input-number',
        prop: 'salePrice',
        label: '销售价（元）',
        min: 0.01,
        max: 99999.99,
        precision: 2,
        required: true,
        span: 8
      },
      {
        type: 'input-number',
        prop: 'linePrice',
        label: '划线价（元）',
        min: 0.01,
        max: 99999.99,
        precision: 2,
        required: true,
        span: 8
      },
      {
        type: 'switch',
        prop: 'showCouponStyle',
        label: '展示成优惠券',
        activeValue: 'Y',
        inactiveValue: 'N',
        required: false,
        span: 9
      },
      // 商品类型(0:普通商品 1:会员商品)
      {
        type: 'select',
        prop: 'productType',
        label: '商品类型',
        labelWidth: 200,
        options: [
          {
            label: '普通商品',
            value: 0
          },
          {
            label: '会员礼包商品',
            value: 1
          }
        ],
        default: 0,
        required: false,
        disabled: isEdit,
        span: 15
      },
      {
        label: '商品图片',
        type: 'upload',
        prop: 'imageUrls',
        multiple: true,
        accept: '.jpeg,.jpg,.png,.gif',
        uploadAttrs: {
          dialogTitle: '上传文件',
          tip: '支持jpg、png、jpeg、gif格式，尺寸要求1:1，大小1M以内，最多10张',
          sizeLimit: 1
        }, // 限制1M
        // listAttrs: { enhance: true }, // 增强属性不支持gif
        listType: 'image', // 图片格式
        countLimit: 10,
        autoHeight: true,
        required: true
      },
      {
        label: '商品视频',
        type: 'upload',
        prop: 'videoUrl',
        multiple: false,
        accept: '.mp4',
        uploadAttrs: { dialogTitle: '上传文件', tip: '支持mp4格式，大小10M以内，最多1条', sizeLimit: 10 }, // 限制10M
        listAttrs: { enhance: true },
        listType: 'video',
        countLimit: 1,
        autoHeight: true
      },
      {
        type: 'textarea',
        prop: 'productSubName',
        label: '商品文案',
        rows: 4,
        resize: 'none',
        maxlength: 100,
        autoHeight: true,
        span: 24
      },
      {
        type: 'editor',
        prop: 'productDetail',
        label: '商品图文描述',
        autoHeight: true,
        span: 24
      }
    ],

    beforeSubmit: async ({ app, data }: PageContext, model: any, options: any) => {
      // 主图取第一个
      data.mainImageUrl = data?.imageUrls?.[0] || ''

      if (data.creator) delete data.creator
      if (data.gmtCreate) delete data.gmtCreate
      if (data.modifier) delete data.modifier
      if (data.gmtModified) delete data.gmtModified

      return true
    }
  }
}

// 日志对话框
export const logDialog = {
  width: 600,
  top: '10vh',
  labelWidth: 0,
  itemSpan: 24,

  onShow: async ({ app, data }: PageContext) => {
    const res = await app.request({
      api: '/cabinet/admin/base/AdminBaseCommonQueryController/queryAuditLogPage',
      params: { bizNo: data.productCode, logScene: 'pms.product_operate', current: 1, size: 100 }
    })

    return {
      payload: {
        operateLogData: res.data
      }
    }
  },

  formItems: [
    {
      label: '',
      labelWidth: 0,
      type: 'edit-table',
      prop: 'operateLogData',
      span: 24,
      noOperation: true,
      autoHeight: true,
      border: true,
      columns: [
        {
          label: '操作时间',
          minWidth: 150,
          formatter: (row: any) => {
            return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
          }
        },
        { prop: 'logDesc', label: '操作名称', minWidth: 160 },
        { prop: 'creator', label: '操作人', minWidth: 100 }
      ]
    }
  ],

  actions: {
    close: {}
  }
}
